import { useState, useEffect, useCallback, Fragment, useMemo, useRef, useContext } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

// Use unified date utils
import { moment, SYSTEM_TIMEZONE } from '../utils/dateUtils';

import { Box, Button, Typography, TextField, FormControl, InputLabel, Select, MenuItem, InputAdornment } from '@mui/material';

// Icons
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';

// Custom Components
import { Spinner, BrowserTitle, DateSwitcher } from '../components';
import ViewSwitcher from '../components/Appointments/ViewSwitcher';
import CancelledCalendar from '../components/Appointments/CancelledCalendar';
import CancelledWeekView from '../components/Appointments/CancelledWeekView';
import CancelledMonthView from '../components/Appointments/CancelledMonthView';

// Utils
import messages from '../static/messages';

// Services
import appointmentsServices from '../services/appointmentsServices';
import userServices from '../services/userServices';
import settingsServices from '../services/settingsServices';

// Auth context
import { AuthContext } from '../context/AuthProvider';

// Styles
import { button } from '../static/theme-styles';

export const CancelledAppointments = () => {
  const [date, setDate] = useState(moment().format());
  const [view, setView] = useState('day');
  const [appointments, setAppointments] = useState(null);
  const [weekAppointments, setWeekAppointments] = useState([]);
  const [monthAppointments, setMonthAppointments] = useState([]);
  const [queue, setQueue] = useState([]);
  const [techniciansList, setTechniciansList] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [appointmentTypes, setAppointmentTypes] = useState([]);
  
  // Column order (same as main appointments page)
  const [columnOrder, setColumnOrder] = useState([]);

  // Initialization state
  const [isInitialized, setIsInitialized] = useState(false);
  const initializingRef = useRef(false);

  const navigate = useNavigate();
  // moment.tz.setDefault is now configured in dateUtils.js
  
  // Get user data for role check
  const { userData } = useContext(AuthContext);
  const userRole = userData?.role;
  
  // Check access - only admin and dispatcher
  useEffect(() => {
    if (userData && !['admin', 'superadmin', 'dispatcher'].includes(userRole)) {
      toast.error('Access denied');
      navigate('/appointments');
    }
  }, [userData, userRole, navigate]);

  // Initialize all required data
  const initialize = useCallback(async () => {
    if (initializingRef.current) return;
    initializingRef.current = true;
    
    console.log('[CancelledAppointments] Starting initialization...');
    
    try {
      // Load appointment types and column order in parallel
      const [typesRes, columnOrderRes] = await Promise.all([
        appointmentsServices.getAppointmentTypes(false).catch(() => null),
        settingsServices.getColumnOrder().catch(() => null),
      ]);
      
      if (typesRes?.status === 206) {
        setAppointmentTypes(typesRes.data.data);
      }
      
      // Set column order from settings
      if (columnOrderRes?.data?.order) {
        setColumnOrder(columnOrderRes.data.order);
      }
      
      // Load technicians list
      const techResponse = await userServices.getUserByRole('technician');
      let technicians = [];
      
      if (techResponse?.status === 206) {
        technicians = techResponse.data.data.map((tech) => ({
          tech_id: tech.id,
          name: `${tech.firstName} ${tech.lastName}`,
          color: tech.preferences?.color || 'red',
          photo: tech.photo?.url,
          createdAt: tech.createdAt,
        }));

        // Get main dispatcher
        try {
          const mainDispatcherResponse = await userServices.getMainDispatcher();
          if (mainDispatcherResponse.status === 206 && mainDispatcherResponse.data.data?.[0]) {
            const mainDispatcher = mainDispatcherResponse.data.data[0];
            const existingIndex = technicians.findIndex(t => t.tech_id === mainDispatcher.id);
            if (existingIndex >= 0) {
              technicians.splice(existingIndex, 1);
            }
            technicians.unshift({
              tech_id: mainDispatcher.id,
              name: `${mainDispatcher.firstName} ${mainDispatcher.lastName}`,
              color: mainDispatcher.preferences?.color || 'green',
              photo: mainDispatcher.photo?.url,
              isMainDispatcher: true,
            });
          }
        } catch (e) {
          console.log('Main dispatcher endpoint not available');
        }
      }
      
      // Apply saved column order if available (same as main appointments page)
      if (columnOrderRes?.data?.order && columnOrderRes.data.order.length > 0) {
        const order = columnOrderRes.data.order;
        technicians.sort((a, b) => {
          const indexA = order.indexOf(a.tech_id);
          const indexB = order.indexOf(b.tech_id);
          if (indexA === -1 && indexB === -1) {
            if (a.isMainDispatcher) return -1;
            if (b.isMainDispatcher) return 1;
            return 0;
          }
          if (indexA === -1) return 1;
          if (indexB === -1) return -1;
          return indexA - indexB;
        });
      } else {
        // Default sort by createdAt
        technicians.sort((a, b) => {
          if (a.isMainDispatcher) return -1;
          if (b.isMainDispatcher) return 1;
          if (a.createdAt < b.createdAt) return -1;
          if (a.createdAt > b.createdAt) return 1;
          return 0;
        });
      }
      
      // Add "Unassigned" column for appointments without lastAssignedUser (always last)
      technicians.push({
        tech_id: null,
        name: 'Unassigned',
        color: '#808080',
        isUnassigned: true,
      });
      
      setTechniciansList(technicians);
      
      console.log('[CancelledAppointments] Initialization complete');
      setIsInitialized(true);
      
    } catch (error) {
      console.error('[CancelledAppointments] Initialization error:', error);
      toast.error(messages.errors.error_data_loaning);
      setIsInitialized(true);
    }
  }, []);

  // Load appointments for current view
  const loadAppointments = useCallback(async () => {
    if (!isInitialized) return;
    
    setLoading(true);
    try {
      // Also load queue for restore functionality
      const queueResponse = await appointmentsServices.getAllAppointments(null, null, true);
      if (queueResponse.status === 206) {
        setQueue(queueResponse.data.data || []);
      }
      
      if (view === 'day') {
        const aptsResponse = await appointmentsServices.getCancelledAppointments(
          date.split('T')[0] + 'T13:00:00.000Z'
        );
        
        if (aptsResponse.status === 206) {
          let appointments_la_time = aptsResponse.data.data.map((el) => ({
            ...el,
            startTime: el.startTime ? moment(el.startTime).tz(SYSTEM_TIMEZONE).format() : null,
            endTime: el.endTime ? moment(el.endTime).tz(SYSTEM_TIMEZONE).format() : null,
          }));

          if (typeFilter) {
            appointments_la_time = appointments_la_time.filter(apt => apt.type?.id === typeFilter);
          }
          if (searchQuery) {
            const query = searchQuery.toLowerCase();
            appointments_la_time = appointments_la_time.filter(apt => 
              apt.property?.address?.toLowerCase().includes(query) ||
              apt.property?.zipCode?.toLowerCase().includes(query) ||
              apt.client?.fullName?.toLowerCase().includes(query) ||
              apt.notes?.some(n => n.note?.toLowerCase().includes(query))
            );
          }
          setAppointments(appointments_la_time);
        }
        
      } else if (view === 'week') {
        const startOfWeek = moment(date).startOf('week').format('YYYY-MM-DD');
        const endOfWeek = moment(date).endOf('week').format('YYYY-MM-DD');
        
        const response = await appointmentsServices.getCancelledAppointmentsByDateRange(startOfWeek, endOfWeek);
        if (response.status === 206 || response.status === 200) {
          let appointments_la_time = response.data.data.map((el) => ({
            ...el,
            startTime: el.startTime ? moment(el.startTime).tz(SYSTEM_TIMEZONE).format() : null,
            endTime: el.endTime ? moment(el.endTime).tz(SYSTEM_TIMEZONE).format() : null,
          }));
          if (typeFilter) {
            appointments_la_time = appointments_la_time.filter(apt => apt.type?.id === typeFilter);
          }
          if (searchQuery) {
            const query = searchQuery.toLowerCase();
            appointments_la_time = appointments_la_time.filter(apt => 
              apt.property?.address?.toLowerCase().includes(query) ||
              apt.property?.zipCode?.toLowerCase().includes(query)
            );
          }
          setWeekAppointments(appointments_la_time);
        }
        
      } else if (view === 'month') {
        const startOfMonth = moment(date).startOf('month').format('YYYY-MM-DD');
        const endOfMonth = moment(date).endOf('month').format('YYYY-MM-DD');
        
        const response = await appointmentsServices.getCancelledAppointmentsByDateRange(startOfMonth, endOfMonth);
        if (response.status === 206 || response.status === 200) {
          let appointments_la_time = response.data.data.map((el) => ({
            ...el,
            startTime: el.startTime ? moment(el.startTime).tz(SYSTEM_TIMEZONE).format() : null,
            endTime: el.endTime ? moment(el.endTime).tz(SYSTEM_TIMEZONE).format() : null,
          }));
          if (typeFilter) {
            appointments_la_time = appointments_la_time.filter(apt => apt.type?.id === typeFilter);
          }
          setMonthAppointments(appointments_la_time);
        }
      }
    } catch (error) {
      console.error('[CancelledAppointments] Load error:', error);
      toast.error(messages.errors.error_data_loaning);
    } finally {
      setLoading(false);
    }
  }, [isInitialized, date, view, typeFilter, searchQuery]);

  // Initialize on mount
  useEffect(() => {
    initialize();
  }, [initialize]);

  // Load appointments when initialized and dependencies change
  useEffect(() => {
    loadAppointments();
  }, [loadAppointments]);

  // Handle month view day click
  const handleMonthDayClick = (clickedDate) => {
    setDate(moment(clickedDate).format());
    setView('day');
  };

  // Date switcher mode based on view
  const dateSwitcherMode = useMemo(() => {
    switch (view) {
      case 'week': return 'week';
      case 'month': return 'month';
      default: return 'day';
    }
  }, [view]);

  // Build base columns structure
  const baseColumns = useMemo(() => {
    if (!techniciansList) return null;
    
    return techniciansList.map((tech) => ({
      info: tech,
      items: [],
    }));
  }, [techniciansList]);

  // Show spinner until initialized
  if (!isInitialized || !baseColumns) return <Spinner />;

  return (
    <Fragment>
      <Box component="section">
        <BrowserTitle title="Cancelled Appointments | Airmaxx Pro" />

        {/* Top bar */}
        <Box
          sx={{
            mt: 1,
            mb: 2,
            backgroundColor: '#FFF3E0',
            padding: '10px 0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', gap: 2, pl: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/appointments')}
              sx={button('primary', 'secondary')}
            >
              Back to Schedule
            </Button>

            <Typography variant="h6" sx={{ color: '#E65100', fontWeight: 'bold' }}>
              CANCELLED APPOINTMENTS
            </Typography>

            <DateSwitcher date={date} setDate={setDate} mode={dateSwitcherMode} />
            <ViewSwitcher view={view} setView={setView} />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pr: 2 }}>
            <TextField
              size="small"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ minWidth: 150 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Type</InputLabel>
              <Select
                value={typeFilter}
                label="Type"
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <MenuItem value="">
                  <em>All</em>
                </MenuItem>
                {appointmentTypes.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {(searchQuery || typeFilter) && (
              <Button
                size="small"
                onClick={() => {
                  setSearchQuery('');
                  setTypeFilter('');
                }}
              >
                Clear
              </Button>
            )}
          </Box>
        </Box>

        {/* Calendar Views */}
        {view === 'day' && (
          <CancelledCalendar
            baseColumns={baseColumns}
            date={date}
            appointments={appointments}
            queue={queue}
            refreshAppointments={loadAppointments}
            loading={loading}
          />
        )}

        {view === 'week' && (
          <CancelledWeekView
            date={date}
            appointments={weekAppointments}
            technicians={techniciansList}
            refreshAppointments={loadAppointments}
            queue={queue}
          />
        )}

        {view === 'month' && (
          <CancelledMonthView
            date={date}
            appointments={monthAppointments}
            technicians={techniciansList}
            onDateClick={handleMonthDayClick}
          />
        )}
      </Box>
    </Fragment>
  );
};

export default CancelledAppointments;
